import { RowCrudStatus } from "../../common/types";
import {
  Role,
  RoleCreateMutationVariables,
  RoleUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { permissionDisplayId } from "../../permission/store/PermissionCommonStore";

export type RoleValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name_es: string;
  name_en: string;
  name_pt: string;
  __permissions: string[];
  showPermissions: { displayId: string; id: string }[];
  audience: string;
  isForAllTenants: boolean;
};

export type RoleErrorsType = {
  name_es?: { message: string; fieldLabel: string };
  name_en?: { message: string; fieldLabel: string };
  name_pt?: { message: string; fieldLabel: string };
  permissions?: { message: string; fieldLabel: string };
  audience?: { message: string; fieldLabel: string };
  isForAllTenants?: { message: string; fieldLabel: string };
};

export function RoleValuesInit(): RoleValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name_es: "",
    name_en: "",
    name_pt: "",
    __permissions: [],
    showPermissions: [],
    audience: "publisher",
    isForAllTenants: false,
  };
}

export type RoleType = Role | RoleValuesType;

export function roleDisplayId(source: Partial<Role>, _langStore: LangStore) {
  return `${source[_langStore.fieldByLang("name") as keyof RoleType]}`;
}

export function getRoleValues(
  source: RoleType,
  _langStore: LangStore,
  _duplicated: boolean = false
): RoleValuesType {
  const entityValues = RoleValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : roleDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name_es = source.name_es;
  entityValues.name_en = source.name_en;
  entityValues.name_pt = source.name_pt;
  entityValues.showPermissions =
    "permissions" in source && source.permissions
      ? (source.permissions || []).map((x) => {
          return { id: x.id, displayId: permissionDisplayId(x, _langStore) };
        })
      : "showPermissions" in source && source.showPermissions
      ? (source.showPermissions || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__permissions = entityValues.showPermissions.map(
    (x) => x.displayId
  );
  entityValues.audience = source.audience;
  entityValues.isForAllTenants = source.isForAllTenants || false;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getRoleUpdateVariables(
  values: RoleValuesType
): RoleUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name_es: values.name_es,
    name_en: values.name_en,
    name_pt: values.name_pt,
    permissionsIds: values.showPermissions
      ? { set: values.showPermissions.map((x) => x.id) }
      : undefined,
    audience: values.audience,
    isForAllTenants: values.isForAllTenants,
  };
}

export function getRoleCreateVariables(
  values: RoleValuesType
): RoleCreateMutationVariables {
  return {
    name_es: values.name_es,
    name_en: values.name_en,
    name_pt: values.name_pt,
    permissionsIds: values.showPermissions
      ? values.showPermissions.map((x) => x.id)
      : undefined,
    audience: values.audience,
    isForAllTenants: values.isForAllTenants,
  };
}
